import { useEffect, useRef } from 'react';
import CaixaAgendamento from '@/components/agenda/CaixaAgendamento';
import { ESTADOS_VISUAIS } from '@/agenda/confirmacao';
import { horaDeRolagem, rolarAteHoje, rolarAteHora } from '@/agenda/rolagem';

/**
 * A grade horária (VF-3) — no formato de calendário: linhas de horário, uma
 * coluna por dia, caixinha colorida por estado.
 *
 * ★ AS LINHAS SÃO A UNIÃO dos horários dos dias exibidos (vide `gradeVisao.js`),
 * nunca 24 horas.
 *
 * ⚠ TRÊS ESTADOS DE CÉLULA QUE NÃO PODEM SE PARECER:
 *   dia fechado    a clínica não atende avaliação — a coluna inteira, com motivo
 *   sem vaga       o horário não existe NESTE dia (a tarde de um dia que só
 *                  atende de manhã; as 12h de um dia útil)
 *   livre          existe e está vago — e aqui a tela PODE afirmar isso, porque
 *                  esta agenda é a fonte da verdade, não um espelho
 *
 * ⚠ E o rodapé não é enfeite: retorno fora de hora, consulta fora da grade e
 * cancelamento não têm célula onde morar. Sem o rodapé eles SUMIRIAM da tela —
 * regressão contra a lista que a VF-2 mostrava.
 */

const DIAS = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'];

const chaveDeHoje = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function Legenda() {
  return (
    <ul className="agenda-legenda">
      {ESTADOS_VISUAIS.map((e) => (
        <li key={e.id}>
          <span className="agenda-legenda-glifo" style={{ color: e.cor }} aria-hidden="true">
            {e.glifo}
          </span>
          {e.label}
        </li>
      ))}
      <li>
        <span className="agenda-legenda-glifo" aria-hidden="true">
          R
        </span>
        Retorno (não ocupa vaga)
      </li>
    </ul>
  );
}

/**
 * As consultas que não têm célula de vaga onde morar — fora da grade, em hora
 * bloqueada de um dia sem tabela, ou canceladas. Existe para elas nunca sumirem
 * da tela por um detalhe de layout.
 */
function Avulsas({ colunas, ocupado, aoAbrirCaixa, aoAlternarConfirmacao }) {
  const linhas = colunas.flatMap((c) =>
    [...c.fora, ...c.cancelados].map((caixa) => ({ chave: c.chave, data: c.data, caixa })),
  );
  if (linhas.length === 0) return null;
  return (
    <div className="agenda-avulsas">
      <h3 className="secao-titulo">Consultas marcadas neste período</h3>
      {linhas.map(({ data, caixa }) => (
        <div key={caixa.id} className="agenda-avulsa">
          <span className="agenda-avulsa-dia">
            {DIAS[(data.getDay() + 6) % 7]} {data.getDate()} · {caixa.hora}
          </span>
          <CaixaAgendamento caixa={caixa} ocupado={ocupado} aoAbrir={aoAbrirCaixa}
            aoAlternar={aoAlternarConfirmacao} />
        </div>
      ))}
    </div>
  );
}

export default function GradeAgenda({
  grade,
  podeAgendar = false,
  ocupado = false,
  aoAbrirCaixa,
  aoEscolherVaga,
  aoEscolherDia,
  aoAlternarConfirmacao,
}) {
  const hoje = chaveDeHoje();
  const { linhas, colunas } = grade;

  /**
   * ★★ A-3(d) — ABRE ROLADA ATÉ A HORA ATUAL.
   *
   * ⚠ UMA VEZ por montagem, e é por isso que existe o `jaRolou`: sem ele, cada
   * confirmação de consulta (que re-renderiza a grade) jogaria a tela de volta
   * para a hora atual — no meio da rolagem de quem estava olhando outro horário.
   * Isso é pior que não rolar nada.
   *
   * ★ O efeito depende de `linhas.length` porque na primeira passada a grade
   * ainda pode estar vazia (`carregando`), e aí não há linha onde parar.
   */
  const envolve = useRef(null);
  const jaRolou = useRef(false);
  useEffect(() => {
    if (jaRolou.current || !envolve.current || linhas.length === 0) return;
    /**
     * ★★★ A-3 ajuste 2 — ROLA NOS DOIS EIXOS: a hora atual em Y, a coluna de HOJE em X.
     *
     * ⚠⚠ Medido no canário de 11/out: só em Y a grade acertava a HORA e errava o
     * DIA — domingo é a última coluna da semana, e a tela abria na segunda.
     *
     * ★ A marca `jaRolou` é do EIXO VERTICAL (é ele que não pode repetir a cada
     * render, senão confirmar uma consulta joga a tela de volta). O horizontal
     * anda junto, na mesma passada — são o mesmo gesto de "abrir onde eu estou".
     */
    const rolouY = rolarAteHora(envolve.current, horaDeRolagem(linhas));
    rolarAteHoje(envolve.current);
    if (rolouY) jaRolou.current = true;
  }, [linhas.length]);
  const temFora = colunas.some((c) => c.fora.length > 0);
  const temCancelados = colunas.some((c) => c.cancelados.length > 0);

  // ★ Sem NENHUMA linha não há tabela onde pintar as células — e era aqui que o
  // MOTIVO do bloqueio se perdia: no dia, um feriado zera a união de horários e
  // a tela caía num "nenhum horário configurado" genérico. São coisas
  // diferentes: "a clínica fechou nesta data, por isto" não é "ninguém
  // configurou a grade". Quem lê a segunda vai mexer em Configurações à toa.
  if (linhas.length === 0) {
    const todosFechados = colunas.length > 0 && colunas.every((c) => c.fechado);
    return (
      <div className="cartao">
        {todosFechados ? (
          <ul className="agenda-fechados">
            {colunas.map((c) => (
              <li key={c.chave}>
                <strong>
                  {DIAS[(c.data.getDay() + 6) % 7]} {c.data.getDate()}
                </strong>{' '}
                — {c.motivo ? `fechado — ${c.motivo}` : 'fechado'}
              </li>
            ))}
          </ul>
        ) : (
          <p className="em-breve">
            Nenhum horário de avaliação configurado para este período. A grade é definida em
            Configurações → Agenda.
          </p>
        )}

        {/* ★ SEM TABELA, O RODAPÉ AINDA PRECISA APARECER. Este retorno
            antecipado escondia as consultas que existem: bloquear um dia que já
            tem gente marcada as fazia SUMIR da tela — e foi o que aconteceu em
            produção quando a semana inteira ficou fechada. Um dia sem vaga não é
            um dia sem consulta. */}
        <Avulsas colunas={colunas} ocupado={ocupado} aoAbrirCaixa={aoAbrirCaixa}
          aoAlternarConfirmacao={aoAlternarConfirmacao} />
      </div>
    );
  }

  return (
    <>
      <Legenda />
      <div className="agenda-grade-envolve" ref={envolve}>
        {/* ★★★ A-3 ajuste 1 — COMPACTA quando há mais de uma coluna.
            ⚠ Derivado de `colunas.length`, e não de uma prop `visao`: o aperto é
            necessário por CAUSA das sete colunas, e o DIA (uma coluna só) não
            deve ser espremido — a regra fica onde está o motivo. */}
        <table className={`agenda-grade${colunas.length > 1 ? ' agenda-grade--compacta' : ''}`}>
          <thead>
            <tr>
              <th className="agenda-calha" scope="col">
                <span className="visualmente-oculto">Horário</span>
              </th>
              {colunas.map((c) => (
                /**
                 * ★★★ A-3 (12/out) — COLUNA ESTREITA no dia fechado e vazio.
                 * ★ `c.estreitavel` vem de `gradeVisao` (fechado pelo horário da
                 * clínica, **não** por exceção, e sem consulta nenhuma).
                 * ⚠ Com `table-layout: fixed`, fixar a largura DESTE `th` devolve
                 * o espaço aos outros — é assim que o domingo some e os dias com
                 * consulta ganham.
                 */
                <th
                  key={c.chave}
                  scope="col"
                  className={[
                    c.chave === hoje ? 'agenda-hoje' : '',
                    c.estreitavel ? 'agenda-coluna-estreita' : '',
                  ].filter(Boolean).join(' ') || undefined}
                >
                  <button type="button" className="btn-texto" onClick={() => aoEscolherDia?.(c.data)}>
                    {DIAS[(c.data.getDay() + 6) % 7]} {c.data.getDate()}
                  </button>
                  {/* ★ O resumo do dia vem da MESMA conta que as células, então
                      cabeçalho e grade não podem discordar na tela. */}
                  <span className="agenda-resumo">
                    {c.fechado
                      ? c.motivo
                        ? `fechado — ${c.motivo}`
                        : 'fechado'
                      : `${c.livres} de ${c.vagas} livres`}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {linhas.map((hora, i) => (
              /* ★ A-3(d) — `data-hora` é o endereço da linha para a rolagem.
                 ⚠ Por atributo, não por índice: a ordem das linhas muda com a
                 grade do dia (o sábado tem 12:00, o dia útil não). */
              <tr key={hora} data-hora={hora}>
                <th scope="row" className="agenda-calha">
                  {hora}
                </th>
                {colunas.map((c) => {
                  const celula = c.celulas[i];

                  if (c.fechado) {
                    return <td key={c.chave} className="agenda-celula agenda-celula--fechado" />;
                  }
                  // ★ QUARTO ESTADO: bloqueado HOJE, por um motivo. Não é
                  // "sem vaga configurada" (que significa "nunca existiu aqui"),
                  // e o motivo aparece — a mesma razão do dia fechado: quem lê
                  // "bloqueado — reunião" não liga para o gestor perguntando.
                  if (celula.bloqueado) {
                    const texto = celula.motivo ? `bloqueado — ${celula.motivo}` : 'bloqueado';
                    return (
                      <td key={c.chave} className="agenda-celula agenda-celula--bloqueado" title={texto}>
                        <span className="agenda-bloqueio">{texto}</span>
                        {celula.caixas.map((caixa) => (
                          <CaixaAgendamento key={caixa.id} caixa={caixa} ocupado={ocupado}
                            aoAbrir={aoAbrirCaixa} aoAlternar={aoAlternarConfirmacao} />
                        ))}
                      </td>
                    );
                  }
                  if (celula.semGrade) {
                    return (
                      <td
                        key={c.chave}
                        className="agenda-celula agenda-celula--sem-grade"
                        title="sem vaga configurada"
                      >
                        <span className="visualmente-oculto">sem vaga configurada</span>
                      </td>
                    );
                  }

                  return (
                    <td key={c.chave} className="agenda-celula">
                      {celula.caixas.map((caixa) => (
                        <CaixaAgendamento
                          key={caixa.id}
                          caixa={caixa}
                          ocupado={ocupado}
                          aoAbrir={aoAbrirCaixa}
                          aoAlternar={aoAlternarConfirmacao}
                        />
                      ))}
                      {celula.livres > 0 &&
                        (podeAgendar ? (
                          <button
                            type="button"
                            className="agenda-vaga"
                            onClick={() => aoEscolherVaga?.(c.data, hora)}
                            aria-label={`Agendar ${hora}, ${celula.livres} livre${celula.livres > 1 ? 's' : ''}`}
                          >
                            {celula.livres} livre{celula.livres > 1 ? 's' : ''}
                          </button>
                        ) : (
                          /* A recepção não marca avaliação: `criarAgendamento`
                             escreve no lead, que é `podeVendas`. Oferecer o
                             clique seria oferecer um permission-denied. */
                          <span className="agenda-vaga agenda-vaga--inerte">
                            {celula.livres} livre{celula.livres > 1 ? 's' : ''}
                          </span>
                        ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>

          {(temFora || temCancelados) && (
            <tfoot>
              {temFora && (
                <tr>
                  <th scope="row" className="agenda-calha">
                    Fora da grade
                  </th>
                  {colunas.map((c) => (
                    <td key={c.chave} className="agenda-celula">
                      {c.fora.map((caixa) => (
                        <CaixaAgendamento
                          key={caixa.id}
                          caixa={caixa}
                          ocupado={ocupado}
                          aoAbrir={aoAbrirCaixa}
                          aoAlternar={aoAlternarConfirmacao}
                        />
                      ))}
                    </td>
                  ))}
                </tr>
              )}
              {temCancelados && (
                <tr>
                  <th scope="row" className="agenda-calha">
                    Cancelados
                  </th>
                  {colunas.map((c) => (
                    <td key={c.chave} className="agenda-celula">
                      {/* Cancelado LIBERA a vaga, então não pode ocupar a célula
                          — a célula precisa poder dizer "livre". Mas some da
                          tela se não aparecer em lugar nenhum. */}
                      {c.cancelados.map((caixa) => (
                        <CaixaAgendamento
                          key={caixa.id}
                          caixa={caixa}
                          ocupado={ocupado}
                          aoAbrir={aoAbrirCaixa}
                          aoAlternar={aoAlternarConfirmacao}
                        />
                      ))}
                    </td>
                  ))}
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}
